import React, { useState, useEffect } from 'react';
import './AvatarImage.css';

// ✅ COMPONENTE OPTIMIZADO: Avatar con carga progresiva y fallbacks
export const AvatarImage = React.memo(({ 
  src, 
  alt, 
  className, 
  size = 'small',
  onClick 
}) => {
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [imageSrc, setImageSrc] = useState(src);

  // Reset states when src changes
  useEffect(() => {
    setImageLoading(true);
    setImageError(false);
    setImageSrc(src);
  }, [src]);

  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
    setImageSrc("/default-avatar.png");
  };

  const sizeClasses = {
    small: 'avatar-small',
    medium: 'avatar-medium',
    large: 'avatar-large'
  };

  // Iniciales para el fallback (ej: "Mariano" -> "M", "Mariano García" -> "MG")
  const getInitials = (name) => {
    if (!name || typeof name !== 'string') return '?';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase().slice(0, 2);
    }
    return (parts[0]?.[0] || '?').toUpperCase();
  };

  const initials = getInitials(alt);
  const showFallback = imageLoading || imageError;

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  return (
    <div 
      className={`avatar-container avatar-size-${size} ${className || ''} ${onClick ? 'clickable' : ''}`}
      onClick={handleClick}
      data-testid="avatar-container"
    >
      {/* Fallback con iniciales: siempre presente, visible mientras carga o si hay error */}
      <div 
        className={`${sizeClasses[size]} avatar-fallback`}
        aria-hidden="true"
        data-testid="avatar-fallback"
        style={{ display: showFallback ? 'flex' : 'none' }}
      >
        <span className="avatar-fallback-initials">{initials}</span>
      </div>

      {/* Skeleton/placeholder mientras carga (animación opcional sobre el fallback) */}
      {imageLoading && (
        <div 
          className={`${sizeClasses[size]} avatar-skeleton`}
          data-testid="avatar-skeleton"
        >
          <div className="avatar-skeleton-pulse"></div>
        </div>
      )}
      
      {/* Imagen real: oculta hasta que cargue para no mostrar icono roto */}
      <img 
        src={imageSrc}
        alt={alt}
        className={`${sizeClasses[size]} ${imageLoading ? 'avatar-loading' : 'avatar-loaded'}`}
        onLoad={handleImageLoad}
        onError={handleImageError}
        style={{
          display: showFallback ? 'none' : 'block',
          transition: 'opacity 0.2s ease-in-out'
        }}
        data-testid="avatar-image"
      />
      
      {/* Badge de foto personalizada */}
    </div>
  );
});

AvatarImage.displayName = 'AvatarImage'; 